import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Modal, ModalHeader, ModalBody } from 'design-system/Modal';
import SelfServeInstructionsPage from 'dashboard/components/SelfServe/Instructions';
import API from '@scale/frontend-lib/dashboard-api';
import InstructionPreview from 'react-scale/components/scalers/instruction';
import { IInstructionJson } from '@scale/shared/projectInstruction/types';
import { IUserJson } from '@scale/shared/types/user';
import { CircularProgress } from 'react-scale/components/design-system';
import { dataNormalizer } from 'react-scale/components/scalers/instruction/utils';

interface InstructionsModalProps {
  rawJSON: string;
  project?: string;
  assignmentId?: string;
  isModalOpen: boolean;
  hideModal: () => void;
  zIndex?: number;
  user?: IUserJson;
  userIsCustomer?: boolean;
}

const InstructionsModalV2: React.FC<InstructionsModalProps> = ({
  rawJSON,
  project,
  assignmentId,
  isModalOpen,
  hideModal,
  zIndex,
  user,
  userIsCustomer,
}) => {
  const [loading, setLoading] = useState(false);
  // if the project doesnt use instructions v2, we fall back to legacy instructions
  const [legacyInstructionObject, setLegacyInstructionObject] = useState<any>();
  const [instructions, setInstructions] = useState<any>();

  useEffect(() => {
    (async () => {
      if (!isModalOpen) return;
      setLoading(true);
      let instructionsRes;
      if (!project) {
        return;
      }
      // TODO: update this once we have our new instructions on the task object
      if (project) {
        try {
          if (assignmentId) {
            instructionsRes = await API.selfserve.getProjectInstructionStatus(
              project,
              assignmentId,
            );
          }

          // get project instructions if task instructions don't exist
          if (
            _.isEmpty(instructionsRes?.instruction?.components) ||
            _.isEmpty(
              dataNormalizer({
                components: instructionsRes?.instruction?.components ?? [],
                isOnboarding: false,
                isCustomer: false,
              }),
            )
          ) {
            instructionsRes = await API.selfserve.fetchDraftProjectInstructionsV2({
              projectId: project,
              project,
              examples: true,
            });
          }
        } catch (e) {
          if (e.status !== 404) {
            return;
          } else {
            instructionsRes = undefined;
          }
        }
      }

      if (instructionsRes && instructionsRes?.instruction) {
        setInstructions(instructionsRes.instruction);
      } else {
        // fall back to instruction object.
        setLegacyInstructionObject(JSON.parse(rawJSON).instructionObjectId);
      }
      setLoading(false);
    })();
  }, [isModalOpen, project, rawJSON]);

  return (
    <Modal
      isOpen={isModalOpen}
      contentLabel="Instructions Modal"
      margin={legacyInstructionObject ? '1% auto 20px' : undefined}
      maxWidth={legacyInstructionObject ? '1100px' : 'auto'}
      maxHeight={legacyInstructionObject ? '97vh' : 'auto'}
      onRequestClose={hideModal}
      zIndex={zIndex}
    >
      <ModalHeader onRequestClose={hideModal}>Instructions</ModalHeader>
      <ModalBody>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <CircularProgress />
          </div>
        ) : legacyInstructionObject ? (
          <div style={{ width: '1000px !important' }}>
            <SelfServeInstructionsPage instructionObjectId={legacyInstructionObject} />
          </div>
        ) : (
          <InstructionPreview
            user={user}
            userIsCustomer={userIsCustomer}
            isRapidPreview={true}
            instructionV2Res={{
              ok: true,
              firstSeenProjectInstruction: false,
              hasSensitiveContent: false,
              param_chunks: false,
              instruction: instructions as IInstructionJson,
            }}
          />
        )}
      </ModalBody>

      <style jsx global>{`
        .modal-instructions-markdown iframe {
          border: 0;
          outline: 0;

          height: 70vh;
          width: 100% !important;
        }
      `}</style>
    </Modal>
  );
};

export default InstructionsModalV2;
